import React, { useEffect, useState } from "react";
import { Alert, Box, Chip, MenuItem, Tooltip, Typography } from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import MedicationIcon from "@mui/icons-material/Medication";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PrintIcon from "@mui/icons-material/Print";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  IpdButton,
  IpdSectionCard,
  IpdTextField,
} from "../../ipd/utils";
import { formatDateTime, truncatedSelectSx } from "../constants";
import { getAssignable, getMedicineFrequencies, placeOrders } from "../api/emergencyApi";
import { detailBlock, escapeHtml, printDocument } from "../utils/print";
import OrderSetDialog from "./OrderSetDialog";
import EmergencyMedicineAutocomplete from "./EmergencyMedicineAutocomplete";
import EmergencyServiceAutocomplete from "./EmergencyServiceAutocomplete";

const GENDER_LABEL = { M: "Male", F: "Female", T: "Other", O: "Other" };

/**
 * Orders placed from the visit (ER-ORD-001…006).
 *
 * Urgent orders sort to the top and are visually distinguished, which is the
 * whole point of marking them: a provider scanning this list is looking for
 * what has not come back yet, and a STAT troponin buried under six routine
 * bloods defeats the marking.
 *
 * Composed as a basket and submitted together, because ordering a panel is one
 * decision. Each line can be marked urgent independently — "STAT bloods" in
 * practice means one of the six, not all of them.
 */
const TYPES = [
  { value: "lab", label: "Laboratory", icon: ScienceIcon },
  { value: "imaging", label: "Imaging", icon: MonitorHeartIcon },
  { value: "medicine", label: "Medicine", icon: MedicationIcon },
];

const ICON = Object.fromEntries(TYPES.map((t) => [t.value, t.icon]));

/**
 * What the composer offers. Laboratory and imaging are one choice here, not
 * two: which of the two an order is comes from the chosen service's own group
 * in the catalogue, so asking first would only create a control that can
 * disagree with where the order actually went. A placed order still reads as
 * "Laboratory" or "Imaging" in the list below — the backend resolves it.
 */
const ORDER_KINDS = [
  { value: "investigation", label: "Test or scan" },
  { value: "medicine", label: "Medicine" },
];

/**
 * What a medicine order is actually for. These are two different acts: a drug
 * pushed now from the department's own stock, whose record is that it was
 * given, and a prescription the patient leaves with, which has to reach a
 * pharmacist. Only the second belongs on the printed prescription.
 */
const GIVE_NOW = "give_now";
const TAKE_HOME = "take_home";
const INTENTS = [
  { value: GIVE_NOW, label: "Give now — from ED stock" },
  { value: TAKE_HOME, label: "To take home — to pharmacy" },
];

// A small fixed clinical route vocabulary — the backend validates against the
// same set of codes.
const ROUTES = [
  { code: "PO", label: "Oral (PO)" },
  { code: "IV", label: "IV" },
  { code: "IM", label: "IM" },
  { code: "SC", label: "SC" },
  { code: "PR", label: "Rectal (PR)" },
  { code: "SL", label: "Sublingual" },
  { code: "INH", label: "Inhaled" },
  { code: "TOP", label: "Topical" },
  { code: "NASAL", label: "Nasal" },
];
const ROUTE_LABEL = Object.fromEntries(ROUTES.map((r) => [r.code, r.label]));

/**
 * What to say about an investigation whose report is not back yet. The
 * receiving department's own status — "Sample Collected", "Processing" — is far
 * more use to a provider chasing a result than a flat "awaiting", so it is
 * shown whenever the department has moved the order at all.
 */
const awaitingLabel = (order) => {
  const status = (order.result?.status || "").trim();
  const noun = order.order_type === "imaging" ? "report" : "result";
  if (!status || status.toLowerCase() === "pending") return `Awaiting ${noun}`;
  return status;
};

const blankLine = () => ({
  order_type: "investigation",
  item_name: "",
  // Set only on an investigation, by picking a catalogue service. An
  // investigation without one cannot be submitted.
  medical_service_id: "",
  // Medicine only. Take-home is the default because it is what every medicine
  // order was before the distinction existed, so nothing changes meaning by
  // being left alone.
  intent: TAKE_HOME,
  instructions: "",
  urgent: false,
  dose: "",
  route: "",
  frequency: "",
  duration: "",
});

export default function OrdersCard({ visit, onPlaced, disabled }) {
  const orders = visit.orders || [];
  // The prescription a patient leaves with is the medicines ordered on the
  // visit — printable while the visit is open and after it closes (ticket 18).
  const medicineOrders = orders.filter((o) => o.order_type === "medicine");
  // What the patient actually leaves with. A dose pushed in resus is not part
  // of a take-home prescription, and printing it as one reads as an
  // instruction to take it again at home. Orders placed before the give-now /
  // take-home distinction existed carry no intent and were all prescriptions,
  // so they stay on the script.
  const takeHomeOrders = medicineOrders.filter((o) => o.intent !== GIVE_NOW);

  const [adding, setAdding] = useState(false);
  const [orderSetOpen, setOrderSetOpen] = useState(false);
  const [lines, setLines] = useState([blankLine()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [frequencies, setFrequencies] = useState([]);
  // The doctor the basket is prescribed for. Defaults to the visit's Emergency
  // Provider — usually right, and the nurse taking a verbal order in resus
  // changes it rather than typing it from scratch.
  const [doctors, setDoctors] = useState([]);
  const [orderingDoctorId, setOrderingDoctorId] = useState(visit.provider_id || "");

  // The frequency master for the Rx dropdown — loaded once, non-blocking: if it
  // fails the dropdown is simply empty and a frequency is left unset.
  useEffect(() => {
    let alive = true;
    getMedicineFrequencies()
      .then((f) => { if (alive) setFrequencies(f); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // The doctors who can be named as prescriber — the same server-filtered list
  // the assign-bed dialog offers. Loaded when the basket opens rather than on
  // mount, since most views of this card never place an order.
  useEffect(() => {
    if (!adding || doctors.length) return;
    let alive = true;
    getAssignable()
      .then((d) => { if (alive) setDoctors(d?.doctors || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [adding, doctors.length]);

  // Keep the default in step if the Emergency Provider is assigned or handed
  // over while this card is on screen.
  useEffect(() => {
    setOrderingDoctorId((current) => current || visit.provider_id || "");
  }, [visit.provider_id]);

  const freqLabel = (code) => frequencies.find((f) => f.code === code)?.label || code;

  const setLine = (index, patch) =>
    setLines(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  /**
   * The take-home prescription: the visit's medicine orders, on the same plain
   * black-on-white sheet the discharge summary and injury report use. Header
   * identifiers and the Emergency Provider carry per ER — either a doctor or a
   * nurse can produce it, since it is a client-side print of data both roles
   * already see.
   */
  const printPrescription = () => {
    const prescriber =
      visit.provider_name ||
      takeHomeOrders.find((m) => m.ordered_by_name)?.ordered_by_name ||
      "—";
    const opened = printDocument({
      title: "Prescription",
      subtitle: `${visit.display_name}${visit.visit_number ? ` · ${visit.visit_number}` : ""}`,
      body: `
        ${detailBlock([
          ["Patient", visit.display_name],
          ["Hospital identifier", visit.patient_id],
          ["Visit number", visit.visit_number],
          ["Age", visit.age ? `${visit.age} years` : ""],
          ["Sex", GENDER_LABEL[visit.gender] || visit.gender],
          ["Emergency Provider", prescriber],
        ])}
        <h2>Medicines (${takeHomeOrders.length})</h2>
        <ol>
          ${takeHomeOrders
            .map((m) => {
              const sig = [
                m.dose,
                m.route ? ROUTE_LABEL[m.route] || m.route : "",
                m.frequency ? freqLabel(m.frequency) : "",
                m.duration,
              ]
                .filter(Boolean)
                .join(" · ");
              const detail = sig || m.instructions || "";
              return (
                `<li><strong>${escapeHtml(m.item_name)}</strong>` +
                `${detail ? ` — ${escapeHtml(detail)}` : ""}` +
                `${
                  m.ordered_by_name
                    ? `<br /><small>Prescribed by ${escapeHtml(m.ordered_by_name)}` +
                      `${m.ordered_at ? " · " + escapeHtml(formatDateTime(m.ordered_at)) : ""}</small>`
                    : ""
                }</li>`
              );
            })
            .join("")}
        </ol>`,
    });
    if (!opened) setError("The print window was blocked — allow pop-ups for this site.");
  };

  // A medicine needs a name; an investigation needs a catalogue service, since
  // without one it reaches no department and its result cannot come back.
  // A medicine given now also needs a dose and a route: the dose is written
  // onto the visit's administration log as a consequence of ordering it, and
  // that record cannot be made without them.
  const lineReady = (l) => {
    if (l.order_type !== "medicine") return Boolean(l.medical_service_id);
    if (!l.item_name.trim()) return false;
    if (l.intent === GIVE_NOW) return Boolean(l.dose.trim() && l.route);
    return true;
  };

  // Every order carries a prescriber, so the basket is not submittable without
  // one — the backend would refuse it anyway when the visit has no provider to
  // fall back to.
  const ready = Boolean(orderingDoctorId) && lines.some(lineReady);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = lines.filter(lineReady).map((l) => {
        const base = {
          order_type: l.order_type,
          item_name: l.item_name.trim(),
          urgent: l.urgent,
        };
        if (l.order_type === "medicine") {
          const giveNow = l.intent === GIVE_NOW;
          return {
            ...base,
            intent: l.intent,
            dose: l.dose.trim() || undefined,
            route: l.route || undefined,
            // A single dose given at the bedside has no schedule; the fields
            // are hidden for it, so do not send whatever they held before.
            frequency: giveNow ? undefined : l.frequency || undefined,
            duration: giveNow ? undefined : l.duration.trim() || undefined,
          };
        }
        // The catalogue service decides whether this is filed to the laboratory
        // or to radiology, and is what lets the result find its way back.
        return {
          ...base,
          medical_service_id: l.medical_service_id,
          instructions: l.instructions.trim() || undefined,
        };
      });
      const result = await placeOrders(visit.id, payload, { orderingDoctorId });
      await onPlaced?.(result.message);

      // A basket can now come back partly placed. Keep exactly what still needs
      // attention in the composer — the lines the server refused, and any the
      // provider had not finished — and clear the ones that were ordered.
      // Clearing everything would make a refused test vanish from the screen
      // that reported it, leaving them to retype it from the message.
      const refusedPositions = new Set((result.rejected || []).map((r) => r.index));
      const submitted = lines.filter(lineReady);
      const remaining = [
        ...submitted.filter((_, i) => refusedPositions.has(i + 1)),
        ...lines.filter((l) => !lineReady(l)),
      ];

      if (remaining.length) {
        setLines(remaining);
      } else {
        setLines([blankLine()]);
        setAdding(false);
      }
    } catch (e) {
      setError(e?.message || "Could not place the orders");
    } finally {
      setBusy(false);
    }
  };

  return (
    <IpdSectionCard
      title="Orders"
      subtitle="Bloods, scans and medicines — urgent first"
      actions={
        takeHomeOrders.length > 0 || (!disabled && !adding) ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            {takeHomeOrders.length > 0 && (
              <IpdButton
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                onClick={printPrescription}
              >
                Print prescription
              </IpdButton>
            )}
            {!disabled && !adding && (
              <IpdButton
                variant="outlined"
                size="small"
                startIcon={<LibraryAddCheckIcon />}
                onClick={() => setOrderSetOpen(true)}
              >
                Use order set
              </IpdButton>
            )}
            {!disabled && !adding && (
              <IpdButton
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setLines([blankLine()]);
                  setError("");
                  setAdding(true);
                }}
              >
                Place orders
              </IpdButton>
            )}
          </Box>
        ) : null
      }
    >
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* ------------------------------------------------------- the basket */}
      {adding && (
        <Box
          sx={{
            mb: 2,
            p: 1.75,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
            border: `1px solid ${IPD_COLORS.border}`,
            bgcolor: IPD_COLORS.cardBg,
          }}
        >
          {/* One prescriber for the basket — composing a panel is one decision
              by one doctor, and a nurse placing it on a verbal order names
              whose order it was. */}
          <Box sx={{ mb: 1.75, maxWidth: 420 }}>
            <IpdTextField
              select
              value={orderingDoctorId}
              onChange={(e) => setOrderingDoctorId(e.target.value)}
              size="small"
              fullWidth
              label="Ordered for"
              sx={{ ...ipdElevatedInputSx, ...truncatedSelectSx }}
              SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
              inputProps={{ "aria-label": "Doctor this is ordered for" }}
              helperText="The doctor these orders are placed for — yourself, or whoever gave the order."
            >
              <MenuItem value="" disabled>
                {doctors.length ? "Name the ordering doctor" : "Loading doctors…"}
              </MenuItem>
              {doctors.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}{d.specialization ? ` — ${d.specialization}` : ""}
                </MenuItem>
              ))}
            </IpdTextField>
          </Box>

          <Box sx={{ display: "grid", gap: 1.5 }}>
            {lines.map((line, index) => (
              <Box
                key={index}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "140px 1fr auto auto" },
                  gap: 1,
                  alignItems: "start",
                }}
              >
                <IpdTextField
                  select
                  value={line.order_type}
                  onChange={(e) =>
                    // Switching kind invalidates whatever was picked — a
                    // service id must never survive onto a medicine line.
                    setLine(index, {
                      order_type: e.target.value,
                      item_name: "",
                      medical_service_id: "",
                    })
                  }
                  size="small"
                  sx={ipdElevatedInputSx}
                  SelectProps={{ MenuProps: getIpdSelectMenuProps() }}
                  inputProps={{ "aria-label": "Order type" }}
                >
                  {ORDER_KINDS.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </IpdTextField>

                <Box sx={{ display: "grid", gap: 1 }}>
                  {line.order_type === "medicine" ? (
                    <>
                      <EmergencyMedicineAutocomplete
                        value={line.item_name}
                        onChange={(v) => setLine(index, { item_name: v })}
                        disabled={disabled}
                      />
                      <IpdTextField
                        select
                        value={line.intent}
                        onChange={(e) => setLine(index, { intent: e.target.value })}
                        size="small"
                        fullWidth
                        sx={ipdElevatedInputSx}
                        SelectProps={{ MenuProps: getIpdSelectMenuProps() }}
                        inputProps={{ "aria-label": "What this medicine is for" }}
                      >
                        {INTENTS.map((i) => (
                          <MenuItem key={i.value} value={i.value}>
                            {i.label}
                          </MenuItem>
                        ))}
                      </IpdTextField>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr 1fr",
                            sm: line.intent === GIVE_NOW ? "1fr 1fr" : "1fr 1fr 1fr 1fr",
                          },
                          gap: 1,
                        }}
                      >
                        <IpdTextField
                          value={line.dose}
                          onChange={(e) => setLine(index, { dose: e.target.value })}
                          placeholder="Dose"
                          size="small"
                          sx={ipdElevatedInputSx}
                          inputProps={{ "aria-label": "Dose" }}
                        />
                        <IpdTextField
                          select
                          value={line.route}
                          onChange={(e) => setLine(index, { route: e.target.value })}
                          size="small"
                          sx={ipdElevatedInputSx}
                          SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                          inputProps={{ "aria-label": "Route" }}
                        >
                          <MenuItem value="">Route</MenuItem>
                          {ROUTES.map((r) => (
                            <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>
                          ))}
                        </IpdTextField>
                        {/* A dose given here and now is one dose. Asking how
                            often and for how long only invites a schedule the
                            emergency department deliberately does not keep. */}
                        {line.intent !== GIVE_NOW && (
                          <>
                            <IpdTextField
                              select
                              value={line.frequency}
                              onChange={(e) => setLine(index, { frequency: e.target.value })}
                              size="small"
                              sx={ipdElevatedInputSx}
                              SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                              inputProps={{ "aria-label": "Frequency" }}
                            >
                              <MenuItem value="">Frequency</MenuItem>
                              {frequencies.map((f) => (
                                <MenuItem key={f.code} value={f.code}>
                                  {f.code}
                                  {f.label && f.label !== f.code ? ` — ${f.label}` : ""}
                                </MenuItem>
                              ))}
                            </IpdTextField>
                            <IpdTextField
                              value={line.duration}
                              onChange={(e) => setLine(index, { duration: e.target.value })}
                              placeholder="Duration"
                              size="small"
                              sx={ipdElevatedInputSx}
                              inputProps={{ "aria-label": "Duration" }}
                            />
                          </>
                        )}
                      </Box>
                    </>
                  ) : (
                    <EmergencyServiceAutocomplete
                      value={
                        line.medical_service_id
                          ? {
                              medicalServiceId: line.medical_service_id,
                              term: line.item_name,
                            }
                          : null
                      }
                      onChange={(service) =>
                        setLine(index, {
                          item_name: service?.term || "",
                          medical_service_id: service?.medicalServiceId || "",
                        })
                      }
                      disabled={disabled}
                    />
                  )}
                </Box>

                <Tooltip title="Mark this one urgent — it rises to the top of the list and the urgency is sent to the receiving department">
                  <Box
                    component="button"
                    type="button"
                    aria-pressed={line.urgent}
                    aria-label="Mark urgent"
                    onClick={() => setLine(index, { urgent: !line.urgent })}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1.25,
                      py: 0.9,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontSize: 12.5,
                      fontWeight: 700,
                      borderRadius: `${IPD_SHAPE.radiusMd}px`,
                      border: `1px solid ${
                        line.urgent ? IPD_COLORS.error.text : IPD_COLORS.border
                      }`,
                      color: line.urgent ? IPD_COLORS.error.text : IPD_COLORS.textMuted,
                      bgcolor: line.urgent ? IPD_COLORS.error.bg : "transparent",
                      "&:focus-visible": {
                        outline: `2px solid ${IPD_COLORS.primary}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <BoltIcon sx={{ fontSize: 15 }} />
                    STAT
                  </Box>
                </Tooltip>

                <Box
                  component="button"
                  type="button"
                  aria-label="Remove this line"
                  disabled={lines.length === 1}
                  onClick={() => setLines(lines.filter((_, i) => i !== index))}
                  sx={{
                    border: "none",
                    background: "none",
                    cursor: lines.length === 1 ? "default" : "pointer",
                    color: lines.length === 1 ? IPD_COLORS.border : IPD_COLORS.textMuted,
                    p: 0.75,
                    "&:hover": {
                      color: lines.length === 1 ? IPD_COLORS.border : IPD_COLORS.error.text,
                    },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1.5,
            }}
          >
            <IpdButton
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLines([...lines, blankLine()])}
            >
              Add another
            </IpdButton>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IpdButton
                variant="outlined"
                onClick={() => {
                  setAdding(false);
                  setError("");
                }}
                disabled={busy}
              >
                Cancel
              </IpdButton>
              <IpdButton onClick={submit} loading={busy} disabled={!ready}>
                Place orders
              </IpdButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* -------------------------------------------------------- the list */}
      {orders.length === 0 ? (
        !adding && (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              px: 2,
              border: `1px dashed ${IPD_COLORS.borderHover}`,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
            }}
          >
            <ScienceIcon sx={{ fontSize: 26, color: IPD_COLORS.textMuted }} />
            <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700 }}>
              Nothing ordered yet
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
              {disabled
                ? "Nothing was ordered on this visit."
                : "Bloods, scans and medicines ordered here go to the laboratory, imaging and pharmacy without leaving the visit."}
            </Typography>
          </Box>
        )
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {orders.map((o) => {
            const Icon = ICON[o.order_type] || ScienceIcon;
            const failed = o.external_status === "failed";
            return (
              <Box
                key={o.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: `${IPD_SHAPE.radiusMd}px`,
                  border: `1px solid ${
                    o.urgent ? IPD_COLORS.error.text : IPD_COLORS.border
                  }`,
                  bgcolor: o.urgent ? IPD_COLORS.error.bg : IPD_COLORS.cardBg,
                }}
              >
                <Icon
                  sx={{
                    fontSize: 18,
                    mt: 0.2,
                    color: o.urgent ? IPD_COLORS.error.text : IPD_COLORS.textMuted,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                      {o.item_name}
                    </Typography>
                    {o.urgent && (
                      <Chip
                        size="small"
                        icon={<BoltIcon sx={{ fontSize: 13 }} />}
                        label="STAT"
                        sx={{
                          height: 19,
                          fontSize: 10.5,
                          fontWeight: 800,
                          color: IPD_COLORS.error.text,
                          bgcolor: "transparent",
                          border: `1px solid ${IPD_COLORS.error.text}`,
                          "& .MuiChip-icon": { color: IPD_COLORS.error.text, ml: 0.5 },
                        }}
                      />
                    )}
                  </Box>
                  {o.instructions && (
                    <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
                      {o.instructions}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted, mt: 0.25 }}>
                    {o.order_type_label}
                    {o.destination ? ` · ${o.destination}` : ""}
                    {o.external_ref ? ` · ${o.external_ref}` : ""}
                    {" · "}
                    {formatDateTime(o.ordered_at)}
                    {o.ordered_by_name ? ` · ${o.ordered_by_name}` : ""}
                    {/* Only when they differ — "placed by the nurse, ordered
                        for the doctor" is the fact worth showing; a doctor
                        ordering for themselves needs no second name. */}
                    {o.ordering_doctor_name &&
                    o.ordering_doctor_name !== o.ordered_by_name
                      ? ` for ${o.ordering_doctor_name}`
                      : ""}
                  </Typography>
                  {/* Result on a lab/imaging order (ticket 18): the report link
                      once it is back, otherwise that it is still awaited. */}
                  {o.result &&
                    (o.result.has_report ? (
                      <Typography
                        sx={{ fontSize: 12, mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <ArticleOutlinedIcon sx={{ fontSize: 14, color: IPD_COLORS.success.text }} />
                        <Box
                          component="a"
                          href={o.result.report_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: IPD_COLORS.primary,
                            fontWeight: 700,
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          View {o.order_type === "imaging" ? "report" : "result"}
                        </Box>
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 11.5,
                          mt: 0.5,
                          color: IPD_COLORS.textMuted,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <HourglassEmptyIcon sx={{ fontSize: 13 }} />
                        {awaitingLabel(o)}
                      </Typography>
                    ))}
                  {failed && (
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        color: IPD_COLORS.error.text,
                        mt: 0.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                      Not received by {o.destination || "the department"} — recorded here
                      and needs chasing
                    </Typography>
                  )}
                </Box>
                {!failed && (
                  <Tooltip title={`Sent to ${o.destination || "the department"}`}>
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 16, color: IPD_COLORS.success.text, mt: 0.3 }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      )}
      <OrderSetDialog
        open={orderSetOpen}
        visit={visit}
        onClose={() => setOrderSetOpen(false)}
        onApplied={onPlaced}
      />
    </IpdSectionCard>
  );
}
